import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/ui-kit/PageBits';
import ChessBoardView from '@/components/chess/ChessBoardView';
import { Sparkles, Volume2, StopCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

function speak(text) {
  if (!window.speechSynthesis) return null;
  window.speechSynthesis.cancel();
  const clean = (text || '').replace(/[#*_`>-]+/g, '').replace(/\s+/g, ' ').trim().slice(0, 800);
  const u = new SpeechSynthesisUtterance(clean);
  u.lang = 'es-ES'; u.rate = 1.0; u.pitch = 1.1;
  const voices = window.speechSynthesis.getVoices();
  const es = voices.find(v => v.lang.startsWith('es'));
  if (es) u.voice = es;
  window.speechSynthesis.speak(u);
  return u;
}

export default function CoachHistory() {
  const [sessions, setSessions] = useState([]);
  const [detail, setDetail] = useState(null);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  useEffect(() => { api.get('/coach/history').then(r => setSessions(r.data)); }, []);
  const open = async (s) => {
    const { data } = await api.get(`/coach/sessions/${s.session_id}`);
    setDetail(data);
  };
  const readAloud = (text, idx) => {
    if (speakingIdx === idx) { window.speechSynthesis?.cancel(); setSpeakingIdx(null); return; }
    const u = speak(text); if (!u) return;
    setSpeakingIdx(idx);
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
  };

  if (detail) {
    return (
      <div className="p-8 max-w-5xl mx-auto">
        <Button data-testid="coach-history-back" variant="ghost" onClick={()=>setDetail(null)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-1"/> Volver
        </Button>
        <PageHeader title={detail.title} subtitle={`Guardado el ${new Date(detail.created_at).toLocaleString()}`}/>
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <ChessBoardView fen={detail.fen} boardWidth={380} arePiecesDraggable={false}/>
          </div>
          <div className="lg:col-span-3 space-y-3">
            {detail.messages.map((m, i) => (
              <div key={i} className={m.role === 'assistant' ? 'bg-white rounded-2xl border border-violet-100 p-4' : 'text-right'}>
                {m.role === 'assistant' ? (
                  <>
                    <div className="flex items-center gap-2 mb-2 text-violet-700 font-bold text-xs uppercase tracking-widest">
                      <Sparkles className="w-3.5 h-3.5"/> Entrenador
                    </div>
                    <div className="whitespace-pre-wrap text-sm text-slate-700 leading-relaxed">{m.content}</div>
                    <div className="mt-2 flex justify-end">
                      <Button onClick={()=>readAloud(m.content, i)} variant="ghost" size="sm" className="h-7 px-2 text-violet-700 hover:bg-violet-100">
                        {speakingIdx === i ? <><StopCircle className="w-3.5 h-3.5 mr-1"/> Parar</> : <><Volume2 className="w-3.5 h-3.5 mr-1"/> Escuchar</>}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="inline-block bg-violet-600 text-white rounded-2xl rounded-br-sm px-4 py-2 text-sm max-w-[85%]">{m.content}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <PageHeader title="Historial del entrenador" subtitle={`${sessions.length} conversaciones guardadas`}/>
      {sessions.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">
          <Sparkles className="w-8 h-8 mx-auto mb-2 text-slate-400"/>
          Cuando le preguntes al entrenador desde el tablero, tus conversaciones quedarán guardadas aquí.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {sessions.map(s => (
            <button key={s.session_id} data-testid={`coach-session-${s.session_id}`} onClick={()=>open(s)}
              className="text-left rounded-2xl bg-white border border-slate-200 p-5 hover:-translate-y-1 hover:shadow-md transition-all">
              <div className="flex items-center gap-2 text-violet-700 text-xs uppercase tracking-widest font-bold mb-2">
                <Sparkles className="w-3.5 h-3.5"/> Entrenador IA
              </div>
              <div className="font-display font-bold line-clamp-2 mb-1">{s.title}</div>
              <div className="text-xs text-slate-500 font-mono truncate">{s.fen}</div>
              <div className="text-xs text-slate-400 mt-2">{new Date(s.updated_at).toLocaleString()}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
